"use client";

/**
 * P5-15 · "Notify me when it's back" on an out-of-stock variant. Signed-in
 * shoppers subscribe in one click (account email); guests enter an email.
 * The parent renders this only when a resolved variant is out of stock and
 * `flags.backInStock` is on.
 */
import * as React from "react";

import { useToast } from "~/components/ui/toast";
import { api } from "~/trpc/react";
import { useTranslator } from "~/i18n/provider";

export function BackInStockControl({
  variantId,
  signedIn,
}: {
  variantId: string;
  signedIn: boolean;
}) {
  const t = useTranslator();
  const { toast } = useToast();
  const status = api.stockAlerts.status.useQuery(
    { variantId },
    { enabled: variantId.length > 0 && signedIn },
  );
  const [email, setEmail] = React.useState("");
  const [subscribed, setSubscribed] = React.useState(false);

  const subscribe = api.stockAlerts.subscribe.useMutation({
    onSuccess: () => {
      setSubscribed(true);
      toast({ title: "We’ll email you when it’s back", tone: "success" });
    },
    onError: (e) =>
      toast({ title: e.message || "Couldn’t set that up.", tone: "danger" }),
  });

  const already = subscribed || status.data?.subscribed;

  if (already) {
    return (
      <p className="mt-3 rounded-md border border-border bg-surface-2/50 px-3 py-2 text-xs text-fg">
        You&rsquo;re on the list — we&rsquo;ll email you the moment this is back
        in stock.
      </p>
    );
  }

  return (
    <div className="mt-3 rounded-md border border-border p-3">
      <p className="text-xs font-semibold text-fg">{t("pdp.outOfStock")}</p>
      <p className="mt-0.5 text-xs text-fg-muted">{t("pdp.notifyNote")}</p>
      {signedIn ? (
        <button
          type="button"
          onClick={() => subscribe.mutate({ variantId })}
          disabled={subscribe.isPending}
          className="mt-2 rounded-md bg-primary px-4 py-2 text-xs font-semibold text-primary-fg hover:opacity-90 disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]"
        >
          {subscribe.isPending ? "Setting up…" : "Email me when it’s back"}
        </button>
      ) : (
        <form
          className="mt-2 flex flex-wrap gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
              toast({ title: "Enter a valid email address.", tone: "danger" });
              return;
            }
            subscribe.mutate({ variantId, email: email.trim() });
          }}
        >
          <label htmlFor="bis-email" className="sr-only">
            {t("pdp.emailAddress")}
          </label>
          <input
            id="bis-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t("pdp.emailPlaceholder")}
            autoComplete="email"
            className="h-9 min-w-0 flex-1 rounded-md border border-border bg-surface px-3 text-sm text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]"
          />
          <button
            type="submit"
            disabled={subscribe.isPending}
            className="h-9 rounded-md bg-primary px-4 text-xs font-semibold text-primary-fg hover:opacity-90 disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]"
          >
            {t("pdp.notifyMe")}
          </button>
        </form>
      )}
    </div>
  );
}
